import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../../api/client.js';
import {
  Confirm,
  DataTable,
  ErrorState,
  Field,
  Modal,
  ReasonModal,
  Spinner,
  StatusBadge
} from '../../components/ui.jsx';
import { StatTile } from '../../components/charts.jsx';
import { useAuth } from '../../state/AuthContext.jsx';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { ServiceForm } from './Utilities.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatDate, formatXaf } from '../../lib/format.js';

/** Utility account states, mapped onto the badge tones the rest of the app uses. */
export const ACCOUNT_BADGE = {
  active: { status: 'approved', label: 'On' },
  cut_off: { status: 'rejected', label: 'Cut off' },
  closed: { status: 'withdrawn', label: 'Closed' }
};

const PasswordHandover = ({ customer, email, password, onClose }) => {
  const toast = useToast();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Copied.');
    } catch {
      toast.info('Copy it by hand: your browser blocked the clipboard.');
    }
  };

  return (
    <Modal
      title="Give this to them"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          I have passed it on
        </button>
      }
    >
      <p className="modal-lead">
        {customer} can now sign in and see their meter readings, their bills and their receipts. They will be made to
        choose their own password first, so this one stops working as soon as they use it.
      </p>

      <div className="handover">
        <span className="handover-label">Email</span>
        <strong>{email}</strong>
        <span className="handover-label">Temporary password</span>
        <code className="handover-code">{password}</code>
      </div>

      <div className="row-actions">
        <button type="button" className="btn btn-outline btn-sm" onClick={copy}>
          Copy the password
        </button>
      </div>

      <p className="notice notice-warn">
        This is the only time it is shown. It has also been emailed to them.
      </p>
    </Modal>
  );
};

const CustomerForm = ({ service, tenancies, loadingLeases, leasesBlocked, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    // A manager restricted to utilities cannot read the landlord's leases, so
    // for them the only way in is the one that needs no lease.
    customerType: leasesBlocked ? 'neighbour' : 'tenant',
    tenancyId: '',
    name: '',
    email: '',
    phone: '',
    label: '',
    meterNumber: '',
    openingReading: '0',
    broughtForwardXaf: '',
    broughtForwardNote: ''
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // Almost always somebody whose meter was replaced: the dial starts again, the
  // arrears do not.
  const [owesAlready, setOwesAlready] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const isTenant = form.customerType === 'tenant';

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit({
        customerType: form.customerType,
        ...(isTenant
          ? { tenancyId: Number(form.tenancyId) }
          : { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() }),
        label: form.label.trim() || null,
        meterNumber: form.meterNumber.trim() || null,
        openingReading: Number(form.openingReading || 0),
        ...(owesAlready && Number(form.broughtForwardXaf) > 0
          ? {
              broughtForwardXaf: Number(form.broughtForwardXaf),
              broughtForwardNote: form.broughtForwardNote.trim() || undefined
            }
          : {})
      });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Put somebody on ${service.name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="utility-customer-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Add them'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <form id="utility-customer-form" className="stack" onSubmit={submit}>
        {leasesBlocked ? null : (
          <Field label="Who is it">
            <select value={form.customerType} onChange={(event) => set({ customerType: event.target.value })}>
              <option value="tenant">One of my tenants</option>
              <option value="neighbour">Somebody who does not rent from me</option>
            </select>
          </Field>
        )}

        {isTenant ? (
          loadingLeases ? (
            <p className="field-hint">Loading your leases...</p>
          ) : tenancies.length === 0 ? (
            <div className="notice notice-info">
              You have no leases in force. Pick the other option to bill somebody who does not rent from you.
            </div>
          ) : (
            <Field label="Which lease" error={errors.tenancyId} required>
              <select value={form.tenancyId} onChange={(event) => set({ tenancyId: event.target.value })} required>
                <option value="">Choose a lease</option>
                {tenancies.map((tenancy) => (
                  <option key={tenancy.id} value={tenancy.id}>
                    {tenancy.tenant?.name} - {tenancy.unit?.label ? `${tenancy.unit.label}, ` : ''}
                    {tenancy.property?.title}
                  </option>
                ))}
              </select>
            </Field>
          )
        ) : (
          <>
            <p className="modal-lead">
              An account will be made for them and a temporary password emailed over, the same way you would add a
              tenant. They do not need a lease with you to be billed for the meter.
            </p>
            <div className="grid-3">
              <Field label="Their name" error={errors.name} required>
                <input type="text" value={form.name} onChange={(event) => set({ name: event.target.value })} required />
              </Field>
              <Field label="Email" error={errors.email} required>
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => set({ email: event.target.value })}
                  required
                />
              </Field>
              <Field label="Phone" error={errors.phone} required>
                <input type="tel" value={form.phone} onChange={(event) => set({ phone: event.target.value })} required />
              </Field>
            </div>
          </>
        )}

        <div className="grid-3">
          <Field
            label="What to call it"
            hint="Back room, the shop, Flat 2. Needed if this person already has another meter here."
            error={errors.label}
          >
            <input type="text" value={form.label} onChange={(event) => set({ label: event.target.value })} />
          </Field>

          <Field label="Meter number" error={errors.meterNumber}>
            <input
              type="text"
              value={form.meterNumber}
              onChange={(event) => set({ meterNumber: event.target.value })}
            />
          </Field>

          {service.billingMode === 'metered' ? (
            <Field
              label="Reading today"
              hint="What the dial says now. Billing starts from here."
              error={errors.openingReading}
            >
              <input
                type="number"
                min="0"
                step="0.001"
                value={form.openingReading}
                onChange={(event) => set({ openingReading: event.target.value })}
              />
            </Field>
          ) : null}
        </div>

        <Field label="Do they already owe you anything?">
          <label className="check-line">
            <input
              type="checkbox"
              checked={owesAlready}
              onChange={(event) => setOwesAlready(event.target.checked)}
            />
            <span>Carry a balance over from before</span>
          </label>
        </Field>

        {owesAlready ? (
          <>
            <div className="notice notice-info">
              <strong>For arrears from a meter that has been replaced.</strong> The new dial starts again, but what
              they owe does not. It goes on as its own bill, drawn up as a draft so you can check the figure before
              they are told, and is not rounded - it is entered exactly as you have it.
            </div>

            <div className="grid-2">
              <Field label="What they already owe, in FCFA" error={errors.broughtForwardXaf} required>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.broughtForwardXaf}
                  onChange={(event) => set({ broughtForwardXaf: event.target.value })}
                  required
                />
              </Field>

              <Field
                label="Where it came from"
                hint="Shown to them on the bill, so the figure is not a surprise."
                error={errors.broughtForwardNote}
              >
                <input
                  type="text"
                  value={form.broughtForwardNote}
                  onChange={(event) => set({ broughtForwardNote: event.target.value })}
                  placeholder="Unpaid on old meter LB231202883, to September 2026"
                />
              </Field>
            </div>
          </>
        ) : null}
      </form>
    </Modal>
  );
};

const monthKey = (date) => date.toISOString().slice(0, 7);

const monthLabel = (iso) =>
  new Date(`${iso}-01T12:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/**
 * Reading the utility's own meter, and what they charged for it.
 *
 * The amount is typed rather than worked out: a utility bill is tiers and taxes,
 * never the dial multiplied by anything, and only the real figure makes the
 * shortfall against the submeters an honest number.
 */
const MainReadingModal = ({ service, month, previousReading, existing, canSetPrevious, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    currentReading: existing?.currentReading != null ? String(existing.currentReading) : '',
    previousReading: String(existing?.previousReading ?? previousReading ?? 0),
    amountXaf: existing?.amount != null ? String(existing.amount) : '',
    note: existing?.note || ''
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const start = `${month}-01`;
  const end = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10);
  const last = canSetPrevious ? Number(form.previousReading || 0) : (existing?.previousReading ?? previousReading ?? 0);
  const units =
    form.currentReading === '' ? null : Math.round((Number(form.currentReading) - last) * 1000) / 1000;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit(
        {
          periodStart: start,
          periodEnd: end,
          currentReading: Number(form.currentReading),
          ...(canSetPrevious ? { previousReading: Number(form.previousReading || 0) } : {}),
          amountXaf: Number(form.amountXaf || 0),
          note: form.note.trim() || null
        },
        photo
      );
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Main meter - ${monthLabel(month)}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="main-reading-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Save the reading'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <p className="modal-lead">
        What {service.name} read at the end of {monthLabel(month)}, and what the utility actually charged you for it.
        The amount comes off their bill, not from your own rate.
      </p>

      <form id="main-reading-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field
            label="Last reading"
            hint={
              canSetPrevious
                ? 'The first month, so this is yours to set: what the dial was really on before this reading. ENEO were billing this meter long before you started recording it. After this it carries forward on its own.'
                : 'Where the main dial stood at the end of the month before.'
            }
            error={errors.previousReading}
          >
            {canSetPrevious ? (
              <input
                type="number"
                min="0"
                step="0.001"
                value={form.previousReading}
                onChange={(event) => set({ previousReading: event.target.value })}
              />
            ) : (
              <input type="text" value={last} readOnly disabled />
            )}
          </Field>

          <Field label="Reading now" error={errors.currentReading} required>
            <input
              type="number"
              min="0"
              step="0.001"
              value={form.currentReading}
              onChange={(event) => set({ currentReading: event.target.value })}
              required
              autoFocus
            />
          </Field>
        </div>

        {units !== null ? (
          <div className={`notice ${units < 0 ? 'notice-bad' : 'notice-info'}`}>
            {units < 0
              ? 'That is lower than the last reading. A meter only runs forward - check the dial.'
              : `${units} ${service.unitLabel} on the main meter this month.`}
          </div>
        ) : null}

        <Field
          label="What the utility charged, in FCFA"
          hint="The total on their bill, including every tier and tax."
          error={errors.amountXaf}
          required
        >
          <input
            type="number"
            min="0"
            step="1"
            value={form.amountXaf}
            onChange={(event) => set({ amountXaf: event.target.value })}
            required
          />
        </Field>

        <Field
          label="Photograph of the dial"
          hint="Tenants on this meter are shown it, so they can see the total being divided."
        >
          <input type="file" accept="image/*" onChange={(event) => setPhoto(event.target.files?.[0] ?? null)} />
        </Field>

        <Field label="Note" error={errors.note}>
          <input type="text" value={form.note} onChange={(event) => set({ note: event.target.value })} />
        </Field>
      </form>
    </Modal>
  );
};

/**
 * The first reading on the main meter.
 *
 * Taken once, when the landlord takes the meter over, and fixed from then on -
 * every month afterwards counts up from it, so a figure that could be edited
 * later would change how much the compound is recorded as having used ever
 * since. The same rule applies to each tenant's submeter, which is why the
 * opening on their account cannot be moved either.
 */
const FirstReadingModal = ({ service, onSubmit, onClose }) => {
  const [reading, setReading] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit(Number(reading || 0));
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`First reading on ${service.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="first-reading-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Record it'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <p className="modal-lead">
        Where the main dial stood when you took this meter over. Every month after this one counts up from it, so it
        is recorded once and cannot be changed afterwards - check it against the dial before you save.
      </p>

      <form id="first-reading-form" className="stack" onSubmit={submit}>
        <Field label={`Reading on the dial, in ${service.unitLabel}`} error={errors.openingReading} required>
          <input
            type="number"
            min="0"
            step="0.001"
            value={reading}
            onChange={(event) => setReading(event.target.value)}
            required
            autoFocus
          />
        </Field>
      </form>
    </Modal>
  );
};

/** One customer's starting figure, while it is still theirs to set. */
const AccountOpeningModal = ({ account, service, onSubmit, onClose }) => {
  const [reading, setReading] = useState(String(account.openingReading ?? 0));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit(Number(reading || 0));
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`First reading for ${account.customer?.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="account-opening-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Save it'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <p className="modal-lead">
        What this submeter was really on when {account.customer?.name} went on it. Their bills count up from here, so
        a meter that was already in use needs its true figure rather than zero. The first bill fixes it.
      </p>

      <form id="account-opening-form" className="stack" onSubmit={submit}>
        <Field label={`Reading on the dial, in ${service.unitLabel}`} error={errors.openingReading} required>
          <input
            type="number"
            min="0"
            step="0.001"
            value={reading}
            onChange={(event) => setReading(event.target.value)}
            required
            autoFocus
          />
        </Field>
      </form>
    </Modal>
  );
};

/** Where the meter started, for the main dial and for everybody on it. */
const FirstReadings = ({ service, accounts, canRecord, onRecord, onSetAccount, onNumberMeter }) => (
  <section className="section-block">
    <div className="section-head">
      <h2>First readings</h2>
      <span className="muted">Where each dial started. Recorded once, then fixed.</span>
    </div>

    {/* The number on the main dial, which is what a tenant can hold the
        landlord's figures against. Prompted for here rather than buried in the
        rate form, because a meter without one cannot be checked by anybody. */}
    {service.meterNumber ? null : (
      <div className="notice notice-info">
        <strong>The main meter has no number against it.</strong> It is the number printed on the dial itself, and on
        the utility's bill - tenants on this meter are shown it, so they can check the total being divided.{' '}
        {canRecord ? (
          <button type="button" className="link-btn" onClick={onNumberMeter}>
            Add it
          </button>
        ) : null}
      </div>
    )}

    {service.openingRecordedAt ? (
      <div className="main-meter-row">
        <div className="cell-stack">
          <strong>
            Main meter{service.meterNumber ? ` ${service.meterNumber}` : ''}: {service.openingReading}{' '}
            {service.unitLabel}
          </strong>
          <span className="muted">
            Recorded {formatDate(service.openingRecordedAt)}. Fixed - every month counts up from here.
          </span>
        </div>
      </div>
    ) : (
      <div className="notice notice-warn">
        <strong>The first reading on the main meter has not been recorded.</strong> Until it is, the meter counts
        from zero - and almost no meter starts at zero, because it was running before you began recording it.{' '}
        {canRecord ? (
          <button type="button" className="link-btn" onClick={onRecord}>
            Record it now
          </button>
        ) : (
          'Ask the landlord to record it.'
        )}
      </div>
    )}

    <DataTable
      columns={[
        { key: 'who', header: 'Customer', render: (account) => account.customer?.name },
        { key: 'meter', header: 'Meter', render: (account) => account.meterNumber || <span className="muted">No number</span> },
        {
          key: 'first',
          header: 'First reading',
          render: (account) => (
            <span>
              {account.openingReading} {service.unitLabel}
            </span>
          )
        },
        {
          key: 'when',
          header: '',
          render: (account) =>
            account.billed ? (
              <span className="muted">Billed from here - fixed</span>
            ) : canRecord ? (
              <button type="button" className="link-btn" onClick={() => onSetAccount(account)}>
                {account.openingRecordedAt ? 'Change it' : 'Set it'}
              </button>
            ) : (
              <span className="muted">Not billed yet</span>
            )
        }
      ]}
      rows={accounts}
      empty={{ title: 'Nobody is on this meter yet', message: 'Add a customer and their first reading is taken then.' }}
    />
  </section>
);

/**
 * Every bill that makes up the month's total, against the main meter.
 *
 * The aggregate on its own answers "did it add up"; this answers "who paid
 * what", which is the question a tenant actually asks. Shown whether or not the
 * main meter has been read, because what everybody was billed is worth seeing
 * either way.
 */
const BillsBehindTheTotal = ({ rows, service, main, total, paid, outstanding, month, elsewhere }) => (
  <DataTable
    columns={[
      {
        key: 'who',
        header: 'Customer',
        render: (row) => (
          <div className="cell-stack">
            <Link to={`/landlord/utilities/accounts/${row.accountId}`} className="cell-title">
              {row.customer}
            </Link>
            <span className="muted">
              {row.reference}
              {row.meterNumber ? ` - meter ${row.meterNumber}` : ''}
            </span>
          </div>
        )
      },
      {
        key: 'units',
        header: 'Used',
        render: (row) =>
          row.unitsUsed === null ? (
            <span className="muted">Fixed charge</span>
          ) : (
            <div className="cell-stack">
              <span>
                {row.unitsUsed} {service.unitLabel}
              </span>
              <span className="muted">
                {row.previousReading} to {row.currentReading}
              </span>
            </div>
          )
      },
      {
        key: 'amount',
        header: 'Their bill',
        render: (row) => (
          <div className="cell-stack">
            <strong>{formatXaf(row.amount)}</strong>
            {row.shareOfBilled === null ? null : (
              <span className="muted">{row.shareOfBilled}% of the month</span>
            )}
            {row.lateFee > 0 ? (
              <span className="overdue-flag">plus {formatXaf(row.lateFee)} late fee</span>
            ) : null}
          </div>
        )
      },
      {
        key: 'paid',
        header: 'Paid',
        render: (row) => (
          <div className="cell-stack">
            <span>{formatXaf(row.paid)}</span>
            {row.outstanding > 0 ? (
              <span className="owing">{formatXaf(row.outstanding)} still owing</span>
            ) : null}
          </div>
        )
      },
      {
        key: 'settled',
        header: '',
        render: (row) =>
          !row.posted ? (
            <StatusBadge status="draft" label="Not sent" />
          ) : row.settled ? (
            <StatusBadge status="approved" label="Paid" />
          ) : row.paid > 0 ? (
            <StatusBadge status="under_review" label="Part paid" />
          ) : (
            <StatusBadge status="pending" label="Not paid" />
          )
      }
    ]}
    rows={rows}
    rowKey={(row) => row.billId}
    empty={{
      title: `Nobody has been billed for ${monthLabel(month)}`,
      message: elsewhere?.total
        ? `This meter has ${elsewhere.total} ${elsewhere.total === 1 ? 'bill' : 'bills'} on it, the most recent in ` +
          `${monthLabel(elsewhere.latestMonth)}. A bill belongs to the month its period starts in, so change the ` +
          `month above to see them.`
        : 'Read the submeters and the bills will appear here beside the main meter.'
    }}
    footer={
      rows.length ? (
        <tr className="total-row">
          <th scope="row">Billed to tenants</th>
          <td>{main ? `${main.unitsUsed} ${service.unitLabel} on the main meter` : ''}</td>
          <td>
            <strong>{formatXaf(total)}</strong>
          </td>
          <td>
            <strong>{formatXaf(paid)}</strong>
          </td>
          <td>
            {outstanding > 0 ? (
              <span className="owing">{formatXaf(outstanding)} still owing</span>
            ) : (
              <span className="muted">All paid</span>
            )}
          </td>
        </tr>
      ) : null
    }
  />
);

/** The main meter set against the submeters on it, for one month. */
const Reconciliation = ({ data, service, month, onRead, canRead, onRemove }) => {
  const { main, submeters, difference, atOwnRate, marginOnTheMeter, unbilledAtOwnRate } = data;
  const elsewhere = data.billsOnTheMeter;

  return (
    <section className="section-block">
      <div className="section-head">
        <h2>Main meter - {monthLabel(month)}</h2>
        <span className="muted">What the utility charged, against what the submeters came to.</span>
      </div>

      {!main ? (
        <>
          <div className="notice notice-info">
            <strong>The main meter has not been read for {monthLabel(month)}.</strong> The submeters come to{' '}
            {formatXaf(submeters.amount)} across {submeters.bills} {submeters.bills === 1 ? 'bill' : 'bills'}. Read the
            main meter to see what the compound actually cost.{' '}
            {canRead ? (
              <button type="button" className="link-btn" onClick={onRead}>
                Read it now
              </button>
            ) : null}
          </div>
          <BillsBehindTheTotal
            rows={submeters.rows || []}
            service={service}
            main={null}
            total={submeters.amount}
            paid={submeters.paid ?? 0}
            outstanding={submeters.outstanding ?? 0}
            month={month}
            elsewhere={elsewhere}
          />
        </>
      ) : (
        <>
          <div className="stat-row">
            <StatTile
              value={formatXaf(main.amount)}
              label="The utility charged"
              note={`${main.unitsUsed} ${service.unitLabel} on the main meter`}
            />
            {/* The whole meter priced the way a tenant is priced. Nobody is
                charged this - it is the figure to hold the utility's bill
                against, and it is what the meter is worth if every unit on it
                were billed on. */}
            <StatTile
              value={formatXaf(atOwnRate ?? 0)}
              label="The whole meter at your rate"
              note={`${main.unitsUsed} ${service.unitLabel} x ${formatXaf(service.unitCost)} per ${service.unitLabel}`}
            />
            <StatTile
              value={formatXaf(submeters.amount)}
              label="Billed to tenants"
              note={`${submeters.units} ${service.unitLabel} across ${submeters.bills} ${
                submeters.bills === 1 ? 'bill' : 'bills'
              }`}
            />
            <StatTile
              value={formatXaf(Math.abs(difference.amount))}
              label={
                difference.kind === 'shortfall'
                  ? 'Not accounted for'
                  : difference.kind === 'surplus'
                    ? 'Collected above cost'
                    : 'Exactly level'
              }
              note={
                difference.kind === 'level'
                  ? 'The submeters match the main meter'
                  : `${Math.abs(difference.units)} ${service.unitLabel}${
                      difference.percentOfUnits !== null ? ` - ${Math.abs(difference.percentOfUnits)}% of the meter` : ''
                    }`
              }
              tone={difference.kind === 'shortfall' ? 'warn' : undefined}
            />
          </div>

          {unbilledAtOwnRate !== null && unbilledAtOwnRate !== 0 ? (
            <p className="field-hint">
              Take everything billed to the submeters off the whole meter at your rate and{' '}
              <strong>
                {formatXaf(Math.abs(unbilledAtOwnRate))} {unbilledAtOwnRate > 0 ? 'is left over' : 'is over-billed'}
              </strong>{' '}
              - {formatXaf(atOwnRate)} on the meter less {formatXaf(submeters.amount)} billed out.{' '}
              {unbilledAtOwnRate > 0
                ? 'That is the value of the units nobody was billed for, at your own rate.'
                : 'The submeters came to more than the whole meter did, which should not happen - check the readings.'}
            </p>
          ) : null}

          {marginOnTheMeter !== null && main.amount > 0 ? (
            <p className="field-hint">
              Every {service.unitLabel} that went through the main meter comes to{' '}
              <strong>{formatXaf(atOwnRate)}</strong> at your rate of {formatXaf(service.unitCost)}, against the{' '}
              {formatXaf(main.amount)} the utility charged for the same units -{' '}
              {marginOnTheMeter > 0
                ? `${formatXaf(marginOnTheMeter)} more than the supply cost you.`
                : marginOnTheMeter < 0
                  ? `${formatXaf(-marginOnTheMeter)} less than the supply cost you, so the rate does not cover it.`
                  : 'exactly what the supply cost you.'}
            </p>
          ) : null}

          {difference.kind === 'shortfall' ? (
            <p className="field-hint">
              {difference.units} {service.unitLabel} went through the main meter without appearing on anybody's
              submeter. On a shared compound that is usually the corridor and security lights, the water pump, and loss
              in the wiring.
            </p>
          ) : difference.kind === 'surplus' ? (
            <p className="field-hint">
              The tenants were billed {formatXaf(-difference.amount)} more than the utility charged. That normally means
              the rate per {service.unitLabel} is set above what the supply actually costs.
            </p>
          ) : null}

          {submeters.drafts > 0 ? (
            <div className="notice notice-warn">
              {submeters.drafts} of these {submeters.bills} bills {submeters.drafts === 1 ? 'is' : 'are'} still a
              draft, so this comparison counts money nobody has been asked for yet.
            </div>
          ) : null}

          <BillsBehindTheTotal
            rows={submeters.rows || []}
            service={service}
            main={main}
            total={submeters.amount}
            paid={submeters.paid ?? 0}
            outstanding={submeters.outstanding ?? 0}
            month={month}
            elsewhere={elsewhere}
          />

          <div className="main-meter-row">
            <div className="cell-stack">
              <span className="muted">
                Read {formatDate(main.readOn)}: {main.previousReading} to {main.currentReading}
                {service.meterNumber ? ` on meter ${service.meterNumber}` : ''}
              </span>
              {main.note ? <span className="muted">{main.note}</span> : null}
              <span className="row-actions">
                {canRead ? (
                  <button type="button" className="link-btn" onClick={onRead}>
                    Correct this reading
                  </button>
                ) : null}
                {/* Throwing the reading away, rather than correcting it, is the
                    landlord's own call - see the module comment on the route. */}
                {onRemove ? (
                  <button type="button" className="link-btn danger-link" onClick={onRemove}>
                    Remove it
                  </button>
                ) : null}
              </span>
            </div>
            {main.photoUrl ? (
              <a href={main.photoUrl} target="_blank" rel="noreferrer" className="meter-photo">
                <img src={main.photoUrl} alt={`The main meter on ${formatDate(main.readOn)}`} />
              </a>
            ) : (
              <span className="muted">No photograph of the dial</span>
            )}
          </div>
        </>
      )}
    </section>
  );
};

const UtilityService = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { utilityKindLabel } = useMeta();
  // What a meter charges is the landlord's decision, not their manager's.
  const { isLandlord } = useAuth();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [handover, setHandover] = useState(null);

  const [month, setMonth] = useState(monthKey(new Date()));
  const [readingMain, setReadingMain] = useState(false);
  // { kind: 'customer' | 'service' | 'reading', account? }
  const [removing, setRemoving] = useState(null);
  const [removingBusy, setRemovingBusy] = useState(false);
  const [recordingFirst, setRecordingFirst] = useState(false);
  const [settingOpening, setSettingOpening] = useState(null);

  const service = useFetch(`/landlord/utility-services/${id}`, [id]);
  // Only loaded while the form is open: most visits to this page are to read it.
  const leases = useFetch(adding ? '/tenancies?status=active' : null, [adding]);
  const reconciliation = useFetch(
    `/landlord/utility-services/${id}/reconciliation?month=${month}-01`,
    [id, month]
  );

  useTitle(service.data?.service.name || 'Utility service');

  if (service.loading && !service.data) return <Spinner />;
  if (service.error) {
    return (
      <div className="container section">
        <ErrorState message={service.error} onRetry={service.reload} />
      </div>
    );
  }

  const { service: row, accounts } = service.data;
  // Submeters hang off the main meter, so there is nothing to put them on until
  // it has been read. Only electricity is held to it - see the route for why.
  const awaitingMainMeter =
    row.kind === 'electricity' && row.billingMode === 'metered' && !row.openingRecordedAt;

  const addCustomer = async (payload) => {
    const result = await api.post(`/landlord/utility-services/${id}/accounts`, payload);
    setAdding(false);
    service.reload();

    if (result.temporaryPassword) {
      setHandover({ customer: payload.name, email: payload.email, password: result.temporaryPassword });
    } else {
      toast.success(
        result.alreadyRegistered ? 'They already had an account, so they are simply on the meter now.' : 'Added.'
      );
    }
  };

  const columns = [
    {
      key: 'who',
      header: 'Customer',
      render: (account) => (
        <div className="cell-stack">
          <Link to={`/landlord/utilities/accounts/${account.id}`} className="cell-title">
            {account.customer?.name}
          </Link>
          <span className="muted">{account.isTenant ? 'Tenant' : 'Not a tenant'}</span>
          {account.label ? <span className="muted">{account.label}</span> : null}
        </div>
      )
    },
    {
      key: 'meter',
      header: 'Meter',
      render: (account) => (
        <div className="cell-stack">
          <span>{account.meterNumber || <span className="muted">No number</span>}</span>
          {row.billingMode === 'metered' ? (
            <span className="muted">
              last read {account.lastReading} {row.unitLabel}
            </span>
          ) : null}
        </div>
      )
    },
    {
      key: 'balance',
      header: 'Owes',
      render: (account) =>
        account.balance?.owed > 0 ? (
          <div className="cell-stack">
            <strong className="owing">{formatXaf(account.balance.owed)}</strong>
            {account.balance.overdue > 0 ? (
              <span className="overdue-flag">{formatXaf(account.balance.overdue)} overdue</span>
            ) : null}
          </div>
        ) : account.balance?.credit > 0 ? (
          <span className="muted">{formatXaf(account.balance.credit)} in credit</span>
        ) : (
          <span className="muted">Nothing owing</span>
        )
    },
    {
      key: 'status',
      header: 'Service',
      render: (account) => <StatusBadge {...ACCOUNT_BADGE[account.status]} />
    },
    {
      key: 'actions',
      header: '',
      render: (account) => (
        <div className="row-actions">
          <Link to={`/landlord/utilities/accounts/${account.id}`} className="btn btn-primary btn-sm">
            Read and bill
          </Link>
          {/* Who is on the meter at all is the landlord's record, not the meter
              reader's: a delete here takes the customer's whole billing history
              with it, so a manager does not get the button. */}
          {isLandlord ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setRemoving({ kind: 'customer', account })}
            >
              Remove
            </button>
          ) : null}
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>{row.name}</h1>
          <p className="muted">
            {utilityKindLabel(row.kind)} -{' '}
            {row.billingMode === 'metered'
              ? `${formatXaf(row.unitCost)} per ${row.unitLabel}`
              : `${formatXaf(row.flatAmount)} each period`}
            {row.standingCharge > 0 ? `, plus ${formatXaf(row.standingCharge)} standing charge` : ''}
            {row.meterNumber ? ` - meter ${row.meterNumber}` : ''}
            {isLandlord ? (
              <>
                {' - '}
                <button type="button" className="link-btn" onClick={() => setEditing(true)}>
                  change the rate or meter number
                </button>
              </>
            ) : null}
          </p>
        </div>
        <div className="row-actions">
          <Link to="/landlord/utilities" className="btn btn-ghost">
            All services
          </Link>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setAdding(true)}
            disabled={awaitingMainMeter}
            title={awaitingMainMeter ? 'Read the main meter first' : undefined}
          >
            Add a customer
          </button>
          <Link to={`/landlord/utilities/${id}/monthly`} className="btn btn-primary">
            Bill everyone for the month
          </Link>
          {isLandlord ? (
            <button type="button" className="btn btn-ghost" onClick={() => setRemoving({ kind: 'service' })}>
              Remove this service
            </button>
          ) : null}
        </div>
      </header>

      {row.billingMode === 'metered' && reconciliation.data ? (
        <>
          <div className="row-actions reconciliation-controls">
            <input
              type="month"
              className="month-picker"
              value={month}
              onChange={(event) => setMonth(event.target.value)}
              aria-label="Month to compare"
            />
            {/* Reading the main dial is the meter reader's job as much as the
                landlord's, so a manager who runs this meter can do it too. */}
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setReadingMain(true)}>
              {reconciliation.data.main ? 'Correct the main reading' : 'Read the main meter'}
            </button>
          </div>
          <Reconciliation
            data={reconciliation.data}
            service={row}
            month={month}
            canRead
            onRead={() => setReadingMain(true)}
            onRemove={isLandlord ? () => setRemoving({ kind: 'reading' }) : null}
          />
        </>
      ) : null}

      {awaitingMainMeter ? (
        <div className="notice notice-warn">
          <strong>Read the main meter before anybody goes on it.</strong> Everything billed off this service counts up
          from that dial, so it is recorded first and fixed from then on.{' '}
          {isLandlord ? (
            <button type="button" className="link-btn" onClick={() => setRecordingFirst(true)}>
              Record it now
            </button>
          ) : (
            'Ask the landlord to record it.'
          )}
        </div>
      ) : null}

      {row.billingMode === 'metered' ? (
        <FirstReadings
          service={row}
          accounts={accounts}
          canRecord={isLandlord}
          onRecord={() => setRecordingFirst(true)}
          onSetAccount={(account) => setSettingOpening(account)}
          onNumberMeter={() => setEditing(true)}
        />
      ) : null}

      <section className="section-block">
        <div className="section-head">
          <h2>Submeters</h2>
          <span className="muted">Everybody billed off this meter.</span>
        </div>
        <DataTable
          columns={columns}
          rows={accounts}
          empty={{
            title: 'Nobody is on this meter yet',
            message: 'Add the tenants and neighbours who use it, and you can start reading and billing.'
          }}
        />
      </section>

      {/* Mounted for as long as the dialog is open, never only once the leases
          have arrived: unmounting it mid-fetch threw away whatever had already
          been typed and silently reset the form back to its first field. */}
      {adding ? (
        <CustomerForm
          service={row}
          tenancies={leases.data?.items || []}
          loadingLeases={leases.loading}
          leasesBlocked={Boolean(leases.error)}
          onSubmit={addCustomer}
          onClose={() => setAdding(false)}
        />
      ) : null}

      {readingMain ? (
        <MainReadingModal
          service={row}
          month={month}
          previousReading={reconciliation.data?.previousMainReading}
          existing={reconciliation.data?.main}
          // Only before the meter has a starting figure at all, and only for
          // the landlord: after that every month carries forward from the one
          // before and nothing typed here is believed.
          canSetPrevious={isLandlord && !row.openingRecordedAt}
          onClose={() => setReadingMain(false)}
          onSubmit={async (payload, photo) => {
            const saved = await api.post(`/landlord/utility-services/${id}/main-readings`, payload);
            if (photo) {
              const body = new FormData();
              body.append('photo', photo);
              await api.upload(`/landlord/utility-main-readings/${saved.reading.id}/photo`, body);
            }
            toast.success('Main meter recorded. The comparison below is now against what you were really charged.');
            setReadingMain(false);
            reconciliation.reload();
            // Reading the first month is also what fixes where the meter
            // started, so the service has to be re-read or the form would go on
            // offering a box whose value is no longer believed.
            service.reload();
          }}
        />
      ) : null}

      {editing ? (
        <ServiceForm
          service={row}
          onClose={() => setEditing(false)}
          onSubmit={async (payload) => {
            await api.patch(`/landlord/utility-services/${id}`, payload);
            toast.success('Saved. Readings from now on use the new rate; bills already raised keep the old one.');
            reconciliation.reload();
            setEditing(false);
            service.reload();
          }}
        />
      ) : null}

      {recordingFirst ? (
        <FirstReadingModal
          service={row}
          onClose={() => setRecordingFirst(false)}
          onSubmit={async (openingReading) => {
            await api.post(`/landlord/utility-services/${id}/opening-reading`, { openingReading });
            toast.success('Recorded. Every month on this meter now counts up from there.');
            setRecordingFirst(false);
            service.reload();
            reconciliation.reload();
          }}
        />
      ) : null}

      {settingOpening ? (
        <AccountOpeningModal
          account={settingOpening}
          service={row}
          onClose={() => setSettingOpening(null)}
          onSubmit={async (openingReading) => {
            await api.patch(`/landlord/utility-accounts/${settingOpening.id}`, { openingReading });
            toast.success('Saved. Their bills count up from there.');
            setSettingOpening(null);
            service.reload();
          }}
        />
      ) : null}

      {removing?.kind === 'customer' ? (
        <ReasonModal
          title={`Take ${removing.account.customer?.name} off ${row.name}`}
          lead={
            `Their bills, readings and meter photographs on this service go with them, and cannot be got back. ` +
            `Receipted money cannot be deleted - if any has been confirmed, close the customer instead. ` +
            `Their sign-in and anything else they hold with you are untouched.`
          }
          label="Why are they being removed"
          confirmLabel="Remove them"
          tone="danger"
          onSubmit={async (reason) => {
            const result = await api.del(`/landlord/utility-accounts/${removing.account.id}`, { body: { reason } });
            toast.success(`${result.removed} is off this meter. What went and why is on your activity feed.`);
            setRemoving(null);
            service.reload();
            reconciliation.reload();
          }}
          onClose={() => setRemoving(null)}
        />
      ) : null}

      {removing?.kind === 'service' ? (
        <ReasonModal
          title={`Remove ${row.name}`}
          lead={
            `Everybody on this meter, every bill and reading against them, and every main meter reading go with it, ` +
            `and cannot be got back. Receipted money cannot be deleted - if any has been confirmed, switch the ` +
            `service off instead by changing the rate screen.`
          }
          label="Why is it being removed"
          confirmLabel="Remove the service"
          tone="danger"
          onSubmit={async (reason) => {
            await api.del(`/landlord/utility-services/${id}`, { body: { reason } });
            toast.success('Removed. What went and why is on your activity feed.');
            setRemoving(null);
            navigate('/landlord/utilities');
          }}
          onClose={() => setRemoving(null)}
        />
      ) : null}

      {removing?.kind === 'reading' ? (
        <Confirm
          title={`Remove the main meter reading for ${monthLabel(month)}`}
          message={
            'The comparison goes back to saying the main meter has not been read for this month. Nobody’s bill ' +
            'changes - the submeters are billed on their own dials. You can read it again at any time.'
          }
          confirmLabel="Remove the reading"
          busy={removingBusy}
          onConfirm={async () => {
            setRemovingBusy(true);
            try {
              await api.del(`/landlord/utility-main-readings/${reconciliation.data.main.id}`);
              toast.success('Removed.');
              setRemoving(null);
              reconciliation.reload();
            } catch (error) {
              toast.error(error.message);
            } finally {
              setRemovingBusy(false);
            }
          }}
          onClose={() => setRemoving(null)}
        />
      ) : null}

      {handover ? (
        <PasswordHandover
          customer={handover.customer}
          email={handover.email}
          password={handover.password}
          onClose={() => setHandover(null)}
        />
      ) : null}
    </div>
  );
};

export default UtilityService;
